import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Search, RefreshCw, Plus, Edit, Trash2, DollarSign, Package, Truck,
  CheckCircle, Clock, Building2, ChevronRight, ChevronDown, Save, X,
  CreditCard, FileText, Globe, Phone, Mail, AlertCircle, ShoppingCart,
  TrendingUp, TrendingDown, BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  Filter, Download, Upload, Eye, ExternalLink, Boxes, Receipt, Wallet,
  CalendarDays, Users, Store, Zap, Settings, MoreHorizontal, Copy,
  CheckCheck, XCircle, Loader2, ImageIcon, Tag, Hash, Percent, LogOut, User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Modern Card Component
const Card = ({ children, className = "", hover = false, onClick }) => (
  <div 
    className={cn(
      "bg-white rounded-2xl border border-gray-100 shadow-sm",
      hover && "hover:shadow-lg hover:border-indigo-200 transition-all duration-300 cursor-pointer",
      className
    )}
    onClick={onClick}
  >
    {children}
  </div>
);

// Modern Button Component
const Button = ({ children, variant = "primary", size = "md", className = "", disabled, loading, ...props }) => {
  const variants = {
    primary: "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-200/50",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    success: "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-200/50",
    danger: "bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600",
    ghost: "bg-transparent text-gray-600 hover:bg-gray-100",
    outline: "border-2 border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
    xl: "px-8 py-4 text-lg",
  };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed active:scale-95",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

// Modern Input Component
const Input = ({ className = "", icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && (
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
    )}
    <input
      className={cn(
        "w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl",
        "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:bg-white",
        "placeholder:text-gray-400 text-gray-800 transition-all duration-200",
        Icon && "pl-12",
        className
      )}
      {...props}
    />
  </div>
);

// Modern Badge Component
const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-gray-100 text-gray-700",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
    info: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };
  return (
    <span className={cn(
      "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold",
      variants[variant],
      className
    )}>
      {children}
    </span>
  );
};

// Modern Select Component
const Select = ({ value, onChange, options, placeholder, className = "" }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={cn(
      "px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl",
      "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400",
      "text-gray-800 cursor-pointer appearance-none transition-all duration-200",
      className
    )}
    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236366F1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '40px' }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

// Modal Component
const Modal = ({ open, onClose, title, description, children, size = "md" }) => {
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl", xl: "max-w-6xl" };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={cn("relative bg-white rounded-3xl shadow-2xl w-full", sizes[size], "max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200")}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "indigo" }) => {
  const colors = {
    indigo: { bg: "from-indigo-500 to-indigo-600", shadow: "shadow-indigo-200/50", light: "bg-indigo-50" },
    emerald: { bg: "from-emerald-500 to-emerald-600", shadow: "shadow-emerald-200/50", light: "bg-emerald-50" },
    amber: { bg: "from-amber-500 to-orange-500", shadow: "shadow-amber-200/50", light: "bg-amber-50" },
    rose: { bg: "from-rose-500 to-pink-500", shadow: "shadow-rose-200/50", light: "bg-rose-50" },
    blue: { bg: "from-blue-500 to-cyan-500", shadow: "shadow-blue-200/50", light: "bg-blue-50" },
    purple: { bg: "from-purple-500 to-violet-500", shadow: "shadow-purple-200/50", light: "bg-purple-50" },
  };
  const c = colors[color];
  return (
    <Card className="p-6 relative overflow-hidden">
      <div className={cn("absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-10", c.light)} />
      <div className="flex items-start justify-between relative">
        <div>
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className={cn("flex items-center gap-1 mt-3 text-sm font-semibold", trend === "up" ? "text-emerald-600" : "text-rose-600")}>
              {trend === "up" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className={cn("p-4 rounded-2xl bg-gradient-to-br shadow-lg", c.bg, c.shadow)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </Card>
  );
};

// Login Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }
    setLoading(true);
    try {
      // Try API auth first, fallback to simple validation
      try {
        const response = await axios.post(`${API}/auth/login`, { username, password });
        if (response.data) {
          localStorage.setItem("supplierHubAuth", JSON.stringify({ username, token: response.data.token || "authenticated" }));
          onLogin({ username });
          toast.success("Welcome to Supplier Hub!");
          return;
        }
      } catch (apiError) {
        // Fallback: simple validation for standalone mode
        if (username === "admin" && password === "admin123") {
          localStorage.setItem("supplierHubAuth", JSON.stringify({ username, token: "standalone" }));
          onLogin({ username });
          toast.success("Welcome to Supplier Hub!");
          return;
        }
      }
      toast.error("Invalid credentials");
    } catch (error) {
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full opacity-20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500 rounded-full opacity-20 blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md p-8 relative">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50 mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Supplier Hub</h1>
          <p className="text-gray-500 mt-2">Purchase & Inventory Management</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
            <Input
              icon={User}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            Sign In
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Integrated with American Distributors LLC
        </p>
      </Card>
    </div>
  );
};

// Main Component
const SupplierHub = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [productStats, setProductStats] = useState({});
  const [payablesSummary, setPayablesSummary] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");

  // Modal states
  const [supplierModal, setSupplierModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
  const [paymentData, setPaymentData] = useState({ amount: 0, payment_method: "Bank Transfer", reference_number: "", notes: "" });

  // Check auth on mount
  useEffect(() => {
    const auth = localStorage.getItem("supplierHubAuth");
    if (auth) {
      const parsed = JSON.parse(auth);
      setIsAuthenticated(true);
      setUser(parsed);
    }
  }, []);

  // Fetch all data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("supplierHubAuth");
    setIsAuthenticated(false);
    setUser(null);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSuppliers(),
        fetchOrders(),
        fetchProducts(),
        fetchStats(),
        fetchProductStats(),
        fetchPayables(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (paymentFilter !== "all") params.append("payment_status", paymentFilter);
      if (searchQuery) params.append("search", searchQuery);
      const res = await axios.get(`${API}/purchase-orders?${params}`);
      setOrders(res.data.orders || []);
    } catch (e) { console.error(e); }
  };

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (searchQuery) params.append("search", searchQuery);
      const res = await axios.get(`${API}/supplier-products?${params}`);
      setProducts(res.data.products || []);
      if (res.data.categories) {
        setProductStats(prev => ({ ...prev, categories: res.data.categories }));
      }
    } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/purchase-orders/stats`);
      setStats(res.data || {});
    } catch (e) { console.error(e); }
  };

  const fetchProductStats = async () => {
    try {
      const res = await axios.get(`${API}/supplier-products/stats`);
      setProductStats(res.data || {});
    } catch (e) { console.error(e); }
  };

  const fetchPayables = async () => {
    try {
      const res = await axios.get(`${API}/finance/payables-summary`);
      setPayablesSummary(res.data);
    } catch (e) { console.error(e); }
  };

  // Refetch on filter changes
  useEffect(() => { if (isAuthenticated) fetchOrders(); }, [statusFilter, paymentFilter, searchQuery]);
  useEffect(() => { if (isAuthenticated) fetchProducts(); }, [categoryFilter, searchQuery]);

  // Sync products from supplier
  const handleSyncProducts = async () => {
    if (!suppliers.length) {
      toast.error("Add a supplier first");
      return;
    }
    setSyncing(true);
    try {
      await axios.post(`${API}/suppliers/${suppliers[0].id}/sync-products`);
      toast.success("Product sync started! This may take 30-60 seconds...");
      setTimeout(() => { fetchProducts(); fetchProductStats(); }, 15000);
      setTimeout(() => { fetchProducts(); fetchProductStats(); setSyncing(false); toast.success("Sync complete!"); }, 45000);
    } catch (e) {
      toast.error("Sync failed");
      setSyncing(false);
    }
  };

  // Save supplier
  const handleSaveSupplier = async () => {
    if (!editingSupplier?.name) {
      toast.error("Supplier name is required");
      return;
    }
    try {
      if (editingSupplier.id) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, editingSupplier);
        toast.success("Supplier updated");
      } else {
        await axios.post(`${API}/suppliers`, editingSupplier);
        toast.success("Supplier created");
      }
      setSupplierModal(false);
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (e) {
      toast.error("Failed to save supplier");
    }
  };

  // Delete supplier
  const handleDeleteSupplier = async (id) => {
    if (!confirm("Delete this supplier?")) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success("Supplier deleted");
      fetchSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  // Order functions
  const openNewOrder = () => {
    setEditingOrder({
      supplier_id: suppliers[0]?.id || "",
      shipping_cost: 0,
      customs_duty: 0,
      other_charges: 0,
      currency: "USD",
      exchange_rate: 280,
      notes: "",
    });
    setOrderItems([{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
    setOrderModal(true);
  };

  const openEditOrder = (order) => {
    setEditingOrder({ ...order });
    setOrderItems(order.items?.length ? order.items : [{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
    setOrderModal(true);
  };

  const addOrderItem = () => setOrderItems([...orderItems, { product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
  const removeOrderItem = (i) => orderItems.length > 1 && setOrderItems(orderItems.filter((_, idx) => idx !== i));
  const updateOrderItem = (i, field, value) => {
    const updated = [...orderItems];
    updated[i] = { ...updated[i], [field]: value };
    setOrderItems(updated);
  };

  const orderSubtotal = useMemo(() => orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0), [orderItems]);
  const orderTotal = useMemo(() => orderSubtotal + (editingOrder?.shipping_cost || 0) + (editingOrder?.customs_duty || 0) + (editingOrder?.other_charges || 0), [orderSubtotal, editingOrder]);

  const handleSaveOrder = async () => {
    if (!editingOrder?.supplier_id) {
      toast.error("Select a supplier");
      return;
    }
    const validItems = orderItems.filter(i => i.product_name);
    if (!validItems.length) {
      toast.error("Add at least one item");
      return;
    }
    try {
      const data = { ...editingOrder, items: validItems };
      if (editingOrder.id) {
        await axios.put(`${API}/purchase-orders/${editingOrder.id}`, data);
        toast.success("Order updated");
      } else {
        await axios.post(`${API}/purchase-orders`, data);
        toast.success("Order created");
      }
      setOrderModal(false);
      setEditingOrder(null);
      fetchOrders();
      fetchStats();
      fetchPayables();
    } catch (e) {
      toast.error("Failed to save order");
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/purchase-orders/${id}/status?status=${status}`);
      toast.success(`Status: ${status}`);
      fetchOrders();
      fetchStats();
      fetchPayables();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!confirm("Delete this order?")) return;
    try {
      await axios.delete(`${API}/purchase-orders/${id}`);
      toast.success("Order deleted");
      fetchOrders();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  // Payment functions
  const openPayment = (order) => {
    setPaymentOrder(order);
    setPaymentData({
      amount: order.total_amount - (order.amount_paid || 0),
      payment_method: "Bank Transfer",
      reference_number: "",
      notes: "",
    });
    setPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (paymentData.amount <= 0) {
      toast.error("Amount must be > 0");
      return;
    }
    try {
      await axios.post(`${API}/purchase-orders/${paymentOrder.id}/payments`, paymentData);
      toast.success("Payment recorded");
      setPaymentModal(false);
      setPaymentOrder(null);
      fetchOrders();
      fetchStats();
      fetchPayables();
    } catch (e) {
      toast.error("Failed to record payment");
    }
  };

  // Add product to order
  const addProductToOrder = (product) => {
    setEditingOrder({
      supplier_id: product.supplier_id,
      shipping_cost: 0,
      customs_duty: 0,
      other_charges: 0,
      currency: "USD",
      exchange_rate: 280,
      notes: "",
    });
    setOrderItems([{ product_name: product.name, sku: product.sku, quantity: 1, unit_cost: product.price }]);
    setOrderModal(true);
  };

  // Navigation items
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "catalog", label: "Product Catalog", icon: Boxes },
    { id: "orders", label: "Purchase Orders", icon: ShoppingCart },
    { id: "suppliers", label: "Suppliers", icon: Building2 },
    { id: "finance", label: "Finance", icon: Wallet },
  ];

  // Status badge helper
  const getStatusBadge = (status) => {
    const map = {
      DRAFT: { variant: "default", label: "Draft" },
      SUBMITTED: { variant: "info", label: "Submitted" },
      CONFIRMED: { variant: "purple", label: "Confirmed" },
      SHIPPED: { variant: "warning", label: "Shipped" },
      RECEIVED: { variant: "success", label: "Received" },
      CANCELLED: { variant: "danger", label: "Cancelled" },
    };
    const s = map[status] || { variant: "default", label: status };
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const map = {
      UNPAID: { variant: "danger", label: "Unpaid" },
      PARTIAL: { variant: "warning", label: "Partial" },
      PAID: { variant: "success", label: "Paid" },
    };
    const s = map[status] || { variant: "default", label: status };
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100/50 sticky top-0 z-40">
          <div className="max-w-[1800px] mx-auto px-8 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
                    Supplier Hub
                  </h1>
                  <p className="text-sm text-gray-500">Purchase & Inventory Management</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={fetchAllData} disabled={loading}>
                  <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                </Button>
                <Button variant="primary" onClick={openNewOrder}>
                  <Plus className="w-4 h-4" /> New Order
                </Button>
                <div className="h-8 w-px bg-gray-200" />
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{user?.username}</span>
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-2 mt-6 -mb-px">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id)}
                  className={cn(
                    "flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-2xl transition-all duration-200",
                    activeView === item.id
                      ? "bg-white text-indigo-600 border-t-2 border-x border-indigo-500 border-gray-100 shadow-sm -mb-px"
                      : "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50/50"
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-[1800px] mx-auto px-8 py-8">
          {/* Dashboard View */}
          {activeView === "dashboard" && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-5 gap-6">
                <StatsCard
                  title="Total Orders"
                  value={stats.total_orders || 0}
                  subtitle="All time"
                  icon={ShoppingCart}
                  color="indigo"
                />
                <StatsCard
                  title="Total Purchases"
                  value={`$${(stats.total_amount || 0).toLocaleString()}`}
                  subtitle="USD"
                  icon={DollarSign}
                  color="emerald"
                />
                <StatsCard
                  title="Amount Paid"
                  value={`$${(stats.total_paid || 0).toLocaleString()}`}
                  subtitle="Settled"
                  icon={CheckCircle}
                  color="blue"
                />
                <StatsCard
                  title="Outstanding"
                  value={`$${(stats.outstanding || 0).toLocaleString()}`}
                  subtitle="Due to suppliers"
                  icon={AlertCircle}
                  color="rose"
                />
                <StatsCard
                  title="Products"
                  value={productStats.total_products || 0}
                  subtitle="In catalog"
                  icon={Boxes}
                  color="purple"
                />
              </div>

              {/* Quick Actions & Recent Orders */}
              <div className="grid grid-cols-3 gap-8">
                {/* Quick Actions */}
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-5">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button variant="outline" className="w-full justify-start" onClick={openNewOrder}>
                      <Plus className="w-4 h-4" /> Create Purchase Order
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setActiveView("catalog")}>
                      <Boxes className="w-4 h-4" /> Browse Catalog
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleSyncProducts} disabled={syncing}>
                      <RefreshCw className={cn("w-4 h-4", syncing && "animate-spin")} />
                      {syncing ? "Syncing..." : "Sync Products from Supplier"}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => { setEditingSupplier({ name: "", email: "", phone: "", website: "", country: "", payment_terms: "" }); setSupplierModal(true); }}>
                      <Building2 className="w-4 h-4" /> Add Supplier
                    </Button>
                  </div>
                </Card>

                {/* Recent Orders */}
                <Card className="p-6 col-span-2">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-gray-900">Recent Orders</h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveView("orders")}>
                      View All <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {orders.slice(0, 5).map(order => (
                      <div key={order.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-indigo-200 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200/50">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{order.po_number}</p>
                            <p className="text-sm text-gray-500">{order.supplier_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">${order.total_amount?.toLocaleString()}</p>
                          <div className="flex items-center gap-2 mt-1 justify-end">
                            {getStatusBadge(order.status)}
                            {getPaymentBadge(order.payment_status)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {!orders.length && (
                      <div className="text-center py-12 text-gray-500">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No orders yet</p>
                        <p className="text-sm">Create your first purchase order</p>
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Payables Summary */}
              {payablesSummary?.payables?.length > 0 && (
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Accounts Payable</h3>
                      <p className="text-sm text-gray-500">Outstanding balances to suppliers</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Total Outstanding</p>
                      <p className="text-3xl font-bold text-rose-600">${payablesSummary.total_outstanding?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {payablesSummary.payables.map(p => (
                      <div key={p.supplier_id} className="p-5 bg-gradient-to-br from-rose-50 to-white rounded-xl border border-rose-100">
                        <p className="font-semibold text-gray-900">{p.supplier_name}</p>
                        <p className="text-2xl font-bold text-rose-600 mt-2">${p.outstanding?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500 mt-1">{p.total_orders} orders • ${p.total_paid?.toLocaleString()} paid</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Catalog View */}
          {activeView === "catalog" && (
            <div className="space-y-6">
              {/* Catalog Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Product Catalog</h2>
                  <p className="text-gray-500 mt-1">{productStats.total_products || 0} products from your suppliers</p>
                </div>
                <Button onClick={handleSyncProducts} disabled={syncing} variant="success" size="lg">
                  <RefreshCw className={cn("w-5 h-5", syncing && "animate-spin")} />
                  {syncing ? "Syncing from American Distributors..." : "Sync Products"}
                </Button>
              </div>

              {/* Filters */}
              <Card className="p-5">
                <div className="flex items-center gap-4">
                  <Input
                    icon={Search}
                    placeholder="Search products by name or SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    options={[
                      { value: "all", label: "All Categories" },
                      ...(productStats.categories || []).map(c => ({ value: c, label: c }))
                    ]}
                    className="w-48"
                  />
                </div>
                {/* Category Pills */}
                {productStats.by_category && Object.keys(productStats.by_category).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {Object.entries(productStats.by_category).map(([cat, count]) => (
                      <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat === categoryFilter ? "all" : cat)}
                        className={cn(
                          "px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200",
                          categoryFilter === cat
                            ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200/50"
                            : "bg-gray-100 text-gray-700 hover:bg-indigo-100 hover:text-indigo-700"
                        )}
                      >
                        {cat}: {count}
                      </button>
                    ))}
                  </div>
                )}
              </Card>

              {/* Products Grid */}
              <div className="grid grid-cols-4 gap-6">
                {products.map(product => (
                  <Card key={product.id} className="overflow-hidden group" hover>
                    <div className="h-36 bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-50 flex items-center justify-center relative overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <Package className="w-16 h-16 text-indigo-300 group-hover:scale-110 transition-transform duration-300" />
                      )}
                      <div className="absolute top-3 left-3">
                        <Badge variant="indigo">{product.category}</Badge>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-gray-900 line-clamp-2 h-12 group-hover:text-indigo-600 transition-colors">{product.name}</h3>
                      <p className="text-sm text-gray-400 mt-1 font-mono">{product.sku}</p>
                      <div className="flex items-center justify-between mt-4">
                        <div>
                          <p className="text-xl font-bold text-emerald-600">${product.price?.toFixed(2)}</p>
                          {product.original_price > product.price && (
                            <p className="text-sm text-gray-400 line-through">${product.original_price?.toFixed(2)}</p>
                          )}
                        </div>
                        <Button size="sm" onClick={() => addProductToOrder(product)}>
                          <Plus className="w-4 h-4" /> Order
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                {!products.length && (
                  <div className="col-span-4 text-center py-20">
                    <Boxes className="w-16 h-16 mx-auto text-gray-300 mb-5" />
                    <h3 className="text-xl font-semibold text-gray-900">No products yet</h3>
                    <p className="text-gray-500 mb-6">Sync products from your supplier to get started</p>
                    <Button onClick={handleSyncProducts} disabled={syncing} size="lg">
                      <RefreshCw className={cn("w-5 h-5", syncing && "animate-spin")} />
                      Sync Now
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Orders View */}
          {activeView === "orders" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Purchase Orders</h2>
                  <p className="text-gray-500 mt-1">Manage orders to your suppliers</p>
                </div>
                <Button onClick={openNewOrder} size="lg">
                  <Plus className="w-5 h-5" /> New Order
                </Button>
              </div>

              {/* Filters */}
              <Card className="p-5">
                <div className="flex items-center gap-4">
                  <Input
                    icon={Search}
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: "all", label: "All Status" },
                      { value: "DRAFT", label: "Draft" },
                      { value: "SUBMITTED", label: "Submitted" },
                      { value: "CONFIRMED", label: "Confirmed" },
                      { value: "SHIPPED", label: "Shipped" },
                      { value: "RECEIVED", label: "Received" },
                      { value: "CANCELLED", label: "Cancelled" },
                    ]}
                    className="w-44"
                  />
                  <Select
                    value={paymentFilter}
                    onChange={setPaymentFilter}
                    options={[
                      { value: "all", label: "All Payments" },
                      { value: "UNPAID", label: "Unpaid" },
                      { value: "PARTIAL", label: "Partial" },
                      { value: "PAID", label: "Paid" },
                    ]}
                    className="w-44"
                  />
                </div>
              </Card>

              {/* Orders Table */}
              <Card className="overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">PO Number</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Supplier</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Items</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Payment</th>
                      <th className="text-right px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map(order => (
                      <tr key={order.id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="px-6 py-5">
                          <span className="font-bold text-indigo-600">{order.po_number}</span>
                        </td>
                        <td className="px-6 py-5 text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-5">
                          <p className="font-semibold text-gray-900">{order.supplier_name}</p>
                          {order.supplier_order_ref && <p className="text-xs text-gray-500">Ref: {order.supplier_order_ref}</p>}
                        </td>
                        <td className="px-6 py-5">
                          <Badge>{order.items?.length || 0} items</Badge>
                        </td>
                        <td className="px-6 py-5">
                          <p className="font-bold text-gray-900">${order.total_amount?.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">PKR {order.total_amount_local?.toLocaleString()}</p>
                        </td>
                        <td className="px-6 py-5">{getStatusBadge(order.status)}</td>
                        <td className="px-6 py-5">
                          {getPaymentBadge(order.payment_status)}
                          {order.amount_paid > 0 && <p className="text-xs text-gray-500 mt-1">Paid: ${order.amount_paid}</p>}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditOrder(order)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {order.payment_status !== "PAID" && (
                              <Button variant="ghost" size="sm" onClick={() => openPayment(order)}>
                                <CreditCard className="w-4 h-4 text-emerald-600" />
                              </Button>
                            )}
                            {order.status !== "RECEIVED" && order.status !== "CANCELLED" && (
                              <Select
                                value=""
                                onChange={(v) => v && handleUpdateStatus(order.id, v)}
                                options={[
                                  { value: "", label: "→" },
                                  { value: "SUBMITTED", label: "Submitted" },
                                  { value: "CONFIRMED", label: "Confirmed" },
                                  { value: "SHIPPED", label: "Shipped" },
                                  { value: "RECEIVED", label: "Received" },
                                  { value: "CANCELLED", label: "Cancel" },
                                ]}
                                className="w-28 py-1.5 text-xs"
                              />
                            )}
                            {(order.status === "DRAFT" || order.status === "CANCELLED") && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!orders.length && (
                      <tr>
                        <td colSpan={8} className="text-center py-16 text-gray-500">
                          <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                          <p className="font-medium">No purchase orders yet</p>
                          <Button variant="ghost" className="mt-3" onClick={openNewOrder}>Create your first order</Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>
            </div>
          )}

          {/* Suppliers View */}
          {activeView === "suppliers" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Suppliers</h2>
                  <p className="text-gray-500 mt-1">Manage your supplier relationships</p>
                </div>
                <Button onClick={() => { setEditingSupplier({ name: "", email: "", phone: "", website: "", country: "", payment_terms: "", notes: "" }); setSupplierModal(true); }} size="lg">
                  <Plus className="w-5 h-5" /> Add Supplier
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {suppliers.map(supplier => (
                  <Card key={supplier.id} className="p-6" hover>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-200/50">
                          {supplier.name?.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{supplier.name}</h3>
                          <p className="text-sm text-gray-500">{supplier.country || "Unknown location"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingSupplier(supplier); setSupplierModal(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteSupplier(supplier.id)}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-5 space-y-2">
                      {supplier.email && (
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {supplier.email}
                        </div>
                      )}
                      {supplier.phone && (
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {supplier.phone}
                        </div>
                      )}
                      {supplier.website && (
                        <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-indigo-600 hover:underline">
                          <Globe className="w-4 h-4" />
                          Visit Website
                        </a>
                      )}
                    </div>
                    <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Total Purchases</p>
                        <p className="text-lg font-bold text-gray-900">${(supplier.total_purchases || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</p>
                        <p className={cn("text-lg font-bold", supplier.outstanding_balance > 0 ? "text-rose-600" : "text-gray-900")}>
                          ${(supplier.outstanding_balance || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {supplier.payment_terms && (
                      <Badge variant="purple" className="mt-4">{supplier.payment_terms}</Badge>
                    )}
                  </Card>
                ))}
                {!suppliers.length && (
                  <div className="col-span-3 text-center py-20">
                    <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-5" />
                    <h3 className="text-xl font-semibold text-gray-900">No suppliers yet</h3>
                    <p className="text-gray-500 mb-6">Add your first supplier to get started</p>
                    <Button onClick={() => { setEditingSupplier({ name: "", email: "", phone: "", website: "", country: "", payment_terms: "" }); setSupplierModal(true); }} size="lg">
                      <Plus className="w-5 h-5" /> Add Supplier
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Finance View */}
          {activeView === "finance" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Finance Overview</h2>
                <p className="text-gray-500 mt-1">Track payments and outstanding balances</p>
              </div>

              {/* Finance Stats */}
              <div className="grid grid-cols-4 gap-6">
                <StatsCard title="Total Purchases" value={`$${(stats.total_amount || 0).toLocaleString()}`} icon={ShoppingCart} color="indigo" />
                <StatsCard title="Total Paid" value={`$${(stats.total_paid || 0).toLocaleString()}`} icon={CheckCircle} color="emerald" />
                <StatsCard title="Outstanding" value={`$${(payablesSummary?.total_outstanding || 0).toLocaleString()}`} icon={AlertCircle} color="rose" />
                <StatsCard title="Suppliers Owed" value={payablesSummary?.suppliers_with_balance || 0} icon={Building2} color="amber" />
              </div>

              {/* Payables Table */}
              <Card className="overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                  <h3 className="text-lg font-bold text-gray-900">Accounts Payable by Supplier</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Supplier</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Orders</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Total Amount</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Paid</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Outstanding</th>
                      <th className="text-right px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payablesSummary?.payables?.map(p => (
                      <tr key={p.supplier_id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="px-6 py-5 font-semibold text-gray-900">{p.supplier_name}</td>
                        <td className="px-6 py-5"><Badge>{p.total_orders} orders</Badge></td>
                        <td className="px-6 py-5 font-medium">${p.total_amount?.toLocaleString()}</td>
                        <td className="px-6 py-5 text-emerald-600 font-medium">${p.total_paid?.toLocaleString()}</td>
                        <td className="px-6 py-5 font-bold text-rose-600 text-lg">${p.outstanding?.toLocaleString()}</td>
                        <td className="px-6 py-5 text-right">
                          <Button variant="outline" size="sm" onClick={() => { setStatusFilter("all"); setPaymentFilter("UNPAID"); setActiveView("orders"); }}>
                            View Orders
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!payablesSummary?.payables?.length && (
                      <tr>
                        <td colSpan={6} className="text-center py-16 text-gray-500">
                          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                          <p className="font-medium text-lg">All caught up!</p>
                          <p className="text-gray-400">No outstanding payables</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>
            </div>
          )}
        </main>

        {/* Supplier Modal */}
        <Modal open={supplierModal} onClose={() => setSupplierModal(false)} title={editingSupplier?.id ? "Edit Supplier" : "Add Supplier"} size="md">
          {editingSupplier && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier Name *</label>
                <Input
                  value={editingSupplier.name}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                  placeholder="e.g., American Distributors LLC"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <Input
                    value={editingSupplier.email || ""}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                    placeholder="contact@supplier.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <Input
                    value={editingSupplier.phone || ""}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                    placeholder="+1 (630) 422-1915"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                <Input
                  value={editingSupplier.website || ""}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, website: e.target.value })}
                  placeholder="https://supplier.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                  <Input
                    value={editingSupplier.country || ""}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Terms</label>
                  <Select
                    value={editingSupplier.payment_terms || ""}
                    onChange={(v) => setEditingSupplier({ ...editingSupplier, payment_terms: v })}
                    options={[
                      { value: "", label: "Select terms" },
                      { value: "Prepaid", label: "Prepaid" },
                      { value: "Net 15", label: "Net 15" },
                      { value: "Net 30", label: "Net 30" },
                      { value: "Net 60", label: "Net 60" },
                      { value: "COD", label: "COD" },
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={() => setSupplierModal(false)}>Cancel</Button>
                <Button onClick={handleSaveSupplier}>
                  <Save className="w-4 h-4" /> Save Supplier
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Order Modal */}
        <Modal open={orderModal} onClose={() => setOrderModal(false)} title={editingOrder?.id ? `Edit Order - ${editingOrder.po_number}` : "New Purchase Order"} description="Create or edit a purchase order" size="xl">
          {editingOrder && (
            <div className="space-y-6">
              {/* Supplier Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier *</label>
                  <Select
                    value={editingOrder.supplier_id}
                    onChange={(v) => setEditingOrder({ ...editingOrder, supplier_id: v })}
                    options={[
                      { value: "", label: "Select supplier" },
                      ...suppliers.map(s => ({ value: s.id, label: s.name }))
                    ]}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier Reference</label>
                  <Input
                    value={editingOrder.supplier_order_ref || ""}
                    onChange={(e) => setEditingOrder({ ...editingOrder, supplier_order_ref: e.target.value })}
                    placeholder="Invoice #, Order #"
                  />
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">Order Items</label>
                  <Button variant="secondary" size="sm" onClick={addOrderItem}>
                    <Plus className="w-4 h-4" /> Add Item
                  </Button>
                </div>
                <div className="border border-gray-200 rounded-2xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Product Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">SKU</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Qty</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Unit Cost ($)</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Total</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((item, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-4 py-3">
                            <Input
                              value={item.product_name}
                              onChange={(e) => updateOrderItem(i, "product_name", e.target.value)}
                              placeholder="Product name"
                              className="py-2"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={item.sku || ""}
                              onChange={(e) => updateOrderItem(i, "sku", e.target.value)}
                              placeholder="SKU"
                              className="py-2"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateOrderItem(i, "quantity", parseInt(e.target.value) || 0)}
                              min="1"
                              className="py-2"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              value={item.unit_cost}
                              onChange={(e) => updateOrderItem(i, "unit_cost", parseFloat(e.target.value) || 0)}
                              step="0.01"
                              min="0"
                              className="py-2"
                            />
                          </td>
                          <td className="px-4 py-3 font-bold text-gray-900">
                            ${(item.quantity * item.unit_cost).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <Button variant="ghost" size="sm" onClick={() => removeOrderItem(i)}>
                              <X className="w-4 h-4 text-rose-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Costs */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Shipping ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.shipping_cost}
                    onChange={(e) => setEditingOrder({ ...editingOrder, shipping_cost: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Customs ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.customs_duty}
                    onChange={(e) => setEditingOrder({ ...editingOrder, customs_duty: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Other ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.other_charges}
                    onChange={(e) => setEditingOrder({ ...editingOrder, other_charges: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Exchange Rate (PKR)</label>
                  <Input
                    type="number"
                    value={editingOrder.exchange_rate}
                    onChange={(e) => setEditingOrder({ ...editingOrder, exchange_rate: parseFloat(e.target.value) || 1 })}
                  />
                </div>
              </div>

              {/* Total Summary */}
              <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-6">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Subtotal</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">${orderSubtotal.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Total (USD)</p>
                    <p className="text-3xl font-bold text-indigo-600 mt-1">${orderTotal.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Total (PKR)</p>
                    <p className="text-3xl font-bold text-emerald-600 mt-1">₨{(orderTotal * (editingOrder.exchange_rate || 1)).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={() => setOrderModal(false)}>Cancel</Button>
                <Button onClick={handleSaveOrder}>
                  <Save className="w-4 h-4" /> Save Order
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Payment Modal */}
        <Modal open={paymentModal} onClose={() => setPaymentModal(false)} title="Record Payment" description={paymentOrder ? `Payment for ${paymentOrder.po_number}` : ""} size="sm">
          {paymentOrder && (
            <div className="space-y-5">
              <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Total:</span> <span className="font-semibold">${paymentOrder.total_amount?.toLocaleString()}</span></div>
                  <div><span className="text-gray-500">Paid:</span> <span className="font-semibold text-emerald-600">${paymentOrder.amount_paid?.toLocaleString()}</span></div>
                  <div className="col-span-2 pt-2 border-t border-gray-100 mt-2">
                    <span className="text-gray-500">Outstanding:</span>
                    <span className="font-bold text-rose-600 text-lg ml-2">${(paymentOrder.total_amount - (paymentOrder.amount_paid || 0)).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Amount ($) *</label>
                <Input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                <Select
                  value={paymentData.payment_method}
                  onChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}
                  options={[
                    { value: "Bank Transfer", label: "Bank Transfer" },
                    { value: "Credit Card", label: "Credit Card" },
                    { value: "PayPal", label: "PayPal" },
                    { value: "Wire Transfer", label: "Wire Transfer" },
                    { value: "Cash", label: "Cash" },
                  ]}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reference Number</label>
                <Input
                  value={paymentData.reference_number}
                  onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                  placeholder="Transaction #"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={() => setPaymentModal(false)}>Cancel</Button>
                <Button variant="success" onClick={handleRecordPayment}>
                  <CreditCard className="w-4 h-4" /> Record Payment
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default SupplierHub;
